import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'urlOutput'];

  connect() {
    // Initially disable all copy buttons
    this.toggleCopyButtons(false);

    // Pre-fill from server redirect (?output_url=) or direct ?url=, scroll only on landing
    const prefill = this.inputTarget.value.trim();
    if (prefill) {
      this.generateUrls({ scroll: new URLSearchParams(window.location.search).has('output_url') });
    }

    // Listen for input changes to disable buttons if input is cleared
    this.inputTarget.addEventListener('input', () => {
      if (!this.inputTarget.value.trim()) {
        this.toggleCopyButtons(false);
      }
    });
  }

  submit(event) {
    event.preventDefault();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    this.generateUrls({ scroll: true });
  }

  generateUrls({ scroll = false } = {}) {
    const value = this.inputTarget.value.trim();
    const docInfo = this.extractDocInfo(value);
    
    if (!docInfo || !docInfo.id) {
      alert('Please enter a valid Google Docs URL (Sheets, Docs, Slides, Drawings, or Forms)');
      this.toggleCopyButtons(false);
      return;
    }

    const baseUrl = `https://docs.google.com/${docInfo.type}/d/${docInfo.id}`;
    const isSpreadsheet = docInfo.type === 'spreadsheets';

    // Spreadsheet-specific URL types that should be hidden for non-spreadsheets
    const spreadsheetOnlyTypes = ['csv', 'tsv', 'gvizcsv', 'xlsx', 'ods', 'htmlview'];

    // Generate URL formats
    const urls = {};
    
    if (isSpreadsheet) {
      // Spreadsheet-specific formats
      urls.csv = `${baseUrl}/export?format=csv`;
      urls.tsv = `${baseUrl}/export?format=tsv`;
      urls.gvizcsv = `${baseUrl}/gviz/tq?tqx=out:csv`;
      urls.xlsx = `${baseUrl}/export?format=xlsx`;
      urls.ods = `${baseUrl}/export?format=ods`;
      urls.htmlview = `${baseUrl}/htmlview`;

    }
    
    // Universal formats (work for all document types)
    urls.pdf = `${baseUrl}/export?format=pdf`;
    urls.copy = `${baseUrl}/copy`;
    urls.preview = `${baseUrl}/preview`;
    urls.demo = `${baseUrl}/view?rm=demo`;
    urls.templatePreview = `${baseUrl}/template/preview`;
    urls.minimal = `${baseUrl}/view?rm=minimal`;
    urls.embedded = `${baseUrl}/view?rm=embedded`;

    // Show the results section first
    const resultsSection = document.querySelector('[data-results-section]');
    if (resultsSection) {
      resultsSection.classList.remove('hidden');
    }

    // Hide spreadsheet-specific options for non-spreadsheets
    spreadsheetOnlyTypes.forEach(type => {
      const target = this.urlOutputTargets.find(t => t.dataset.urlType === type);
      if (target) {
        if (isSpreadsheet) {
          target.classList.remove('hidden');
        } else {
          target.classList.add('hidden');
        }
      }
    });

    // Update all URL outputs
    Object.keys(urls).forEach(key => {
      const target = this.urlOutputTargets.find(t => t.dataset.urlType === key);
      if (target) {
        const urlDisplay = target.querySelector('[data-url-display]');
        const copyButton = target.querySelector('[data-copy-button]');
        
        if (urlDisplay) {
          urlDisplay.textContent = urls[key];
          urlDisplay.href = urls[key];
        }
        
        if (copyButton) {
          copyButton.dataset.url = urls[key];
        }
        
        target.classList.remove('hidden');
      }
    });
    
    // Enable copy buttons after URLs are generated
    this.toggleCopyButtons(true);
    this.syncEmbeddedPreview(urls.embedded);

    if (scroll && resultsSection) {
      requestAnimationFrame(() => {
        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  syncEmbeddedPreview(embeddedUrl) {
    const section = document.getElementById('embeddedSheetSection');
    const previewLink = document.getElementById('embeddedSheetPreviewLink');
    if (!section) return;

    const hasUrl = Boolean(embeddedUrl);
    section.classList.toggle('hidden', !hasUrl);
    if (previewLink && hasUrl) {
      previewLink.href = embeddedUrl;
    }
  }

  toggleCopyButtons(enabled) {
    const copyButtons = document.querySelectorAll('[data-copy-button]');
    copyButtons.forEach(button => {
      if (enabled) {
        button.disabled = false;
        button.classList.remove('bg-slate-400', 'cursor-not-allowed', 'opacity-50');
        button.classList.add('bg-blue-600', 'hover:bg-blue-700');
      } else {
        button.disabled = true;
        button.classList.remove('bg-blue-600', 'hover:bg-blue-700');
        button.classList.add('bg-slate-400', 'cursor-not-allowed', 'opacity-50');
      }
    });
  }

  openPdfOptions(event) {
    event.preventDefault();
    const value = this.inputTarget.value.trim();
    const docInfo = value ? this.extractDocInfo(value) : null;

    if (docInfo?.id) {
      const baseUrl = `https://docs.google.com/${docInfo.type}/d/${docInfo.id}/`;
      window.location.href = `/pdf?output_url=${encodeURIComponent(baseUrl)}`;
      return;
    }

    window.location.href = '/pdf';
  }

  copyToClipboard(event) {
    event.preventDefault();
    const button = event.currentTarget;
    
    // Don't copy if button is disabled
    if (button.disabled) {
      return;
    }
    
    const url = button.dataset.url;
    
    // Check if URL exists
    if (!url || url === '#') {
      console.error('No URL found on button');
      alert('No URL available to copy');
      return;
    }
    
    // Check if clipboard API is available
    if (!navigator.clipboard) {
      // Fallback for browsers without clipboard API
      this.fallbackCopyToClipboard(url, button);
      return;
    }
    
    navigator.clipboard.writeText(url).then(() => {
      // Show feedback
      const originalText = button.textContent;
      button.textContent = '✓ Copied!';
      button.classList.add('bg-green-600');
      button.classList.remove('bg-blue-600', 'hover:bg-blue-700');
      
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('bg-green-600');
        button.classList.add('bg-blue-600', 'hover:bg-blue-700');
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      // Try fallback method
      this.fallbackCopyToClipboard(url, button);
    });
  }
  
  fallbackCopyToClipboard(text, button) {
    // Fallback method for browsers without clipboard API
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        const originalText = button.textContent;
        button.textContent = '✓ Copied!';
        button.classList.add('bg-green-600');
        button.classList.remove('bg-blue-600', 'hover:bg-blue-700');
        
        setTimeout(() => {
          button.textContent = originalText;
          button.classList.remove('bg-green-600');
          button.classList.add('bg-blue-600', 'hover:bg-blue-700');
        }, 2000);
      } else {
        alert('Failed to copy. Please copy manually: ' + text);
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      alert('Failed to copy. Please copy manually: ' + text);
    } finally {
      document.body.removeChild(textArea);
    }
  }

  extractDocInfo(url) {
    if (!url) return null;

    const normalized = this.normalizeDocUrl(url);

    const patterns = {
      spreadsheets: /\/spreadsheets\/d\/([-\w]{25,})/,
      document: /\/document\/d\/([-\w]{25,})/,
      presentation: /\/presentation\/d\/([-\w]{25,})/,
      drawings: /\/drawings\/d\/([-\w]{25,})/,
      forms: /\/forms\/d\/([-\w]{25,})/
    };
    
    for (const [type, pattern] of Object.entries(patterns)) {
      const match = normalized.match(pattern);
      if (match) {
        return { type, id: match[1] };
      }
    }

    return null;
  }

  normalizeDocUrl(url) {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://docs.google.com/${trimmed.replace(/^\//, '')}`;
  }
}